/**
 * ENG-31236: paired transfer action buttons must render at the same size.
 *
 * Used by two rows:
 * - resident Snapshot: Start Transfer + Process Move-Out
 * - Resident Transfer card: Cancel Transfer + Complete Transfer
 *
 * The buttons are equal flex children of their row rather than sharing a fixed minWidth, so they stay
 * equal whatever the labels say and shrink together on narrow viewports instead of overflowing the
 * xs={12} column (a fixed pair width needs 352px before padding, more than a 375px phone has).
 *
 * Three sx targets are needed because of how the pieces nest:
 *   Stack > Tooltip's own container Box > span wrapper > Button
 * - stackSx: alignSelf:'stretch' is load-bearing wherever the parent Stack sets alignItems:'center',
 *   which sizes the row to max-content — flex children of a max-content row have nothing to
 *   distribute. maxWidth keeps the pair from spanning the full width of a wide column.
 * - tooltipContainerSx: the component library's Tooltip renders
 *   `Tooltip > Box sx={{ width: 'fit-content', ...containerSX }} > children`, so THAT Box — not our
 *   span — is the row's flex child. The flex sizing has to land here via `containerSX` or both buttons
 *   stay content-sized. width:'auto' neutralizes the library's fit-content; minWidth:0 lets each child
 *   shrink below its label width (MUI Button's min-width is 64px) so the label wraps instead of
 *   overflowing.
 * - buttonWrapperSx: the span (the Tooltip needs a non-disabled element to hook, since a disabled MUI
 *   button swallows pointer events) must fill the tooltip container so the fullWidth Button spans it.
 */
export const TRANSFER_ACTION_ROW_MAX_WIDTH = 420;

/**
 * Gap between the two buttons, shared so the Snapshot row and the Transfer card row cannot drift
 * apart (the Snapshot row used spacing={1} until ENG-31236). MUI Stack spacing unit = 8px, so 2 = 16px.
 */
export const TRANSFER_ACTION_ROW_SPACING = 2;

export const buildTransferActionLayout = (showBothButtons: boolean) =>
  showBothButtons
    ? {
        stackSx: {
          alignSelf: 'stretch',
          width: '100%',
          maxWidth: TRANSFER_ACTION_ROW_MAX_WIDTH,
        },
        tooltipContainerSx: {
          display: 'flex',
          flex: '1 1 0',
          minWidth: 0,
          width: 'auto',
        },
        buttonWrapperSx: { display: 'flex', width: '100%' },
        fullWidth: true,
      }
    : {
        stackSx: undefined,
        tooltipContainerSx: undefined,
        buttonWrapperSx: undefined,
        fullWidth: false,
      };
